/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { type JSX } from 'react';
import useStyles from './styles';
import { useTheme } from '@mui/material/styles';
import {
    useMediaQuery,
    Dialog,
    DialogTitle,
    Typography,
    DialogContent,
    DialogActions,
    Button,
    Stack,
} from '@mui/material';

import { CustomTransComponent } from 'components';
/**
 * The above type represents the props for an error modal component in a TypeScript React application.
 * @property {any[]} messages - The `messages` property is an array that contains any error messages
 * that need to be displayed in the error modal.
 * @property {boolean} isVisible - The `isVisible` property is a boolean that determines whether the
 * error modal should be visible or not.
 * @property modalHandler - The `modalHandler` property is a function that takes a boolean parameter
 * and does some action based on that parameter. It is used to handle the visibility of the error
 * modal.
 * @property retryHandler - The `retryHandler` property is a function that takes an argument `e` of
 * type `any` and returns a `Promise` that resolves to `void`. It is used to handle retrying an action
 * or operation when it fails.
 * @property clearPayload - The `clearPayload` property is a function that is used to clear any data or
 * payload related to the error. It does not take any arguments and does not return anything.
 * @property {string | undefined} deviceId - The `deviceId` property is a string that represents the
 * unique identifier of a device. It is optional and can be either a string value or `undefined`.
 */
type ErrorModalProps = {
    messages: any[];
    isVisible: boolean;
    modalHandler: (action: boolean) => void;
    retryHandler: (e: any) => Promise<void>;
    clearPayload: () => void;
    deviceId: string | undefined;
};

/* The code is defining a functional component called `ErrorModal` that renders a dialog box to display
error messages. */
export const ErrorModal = ({ isVisible, modalHandler, ...props }: ErrorModalProps): JSX.Element => {
    const theme: any = useTheme();
    const sm = useMediaQuery(theme.breakpoints.up('sm'));
    // const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const classes = useStyles(theme);

    /**
     * The function `newDeviceHandler` closes a modal and clears the payload.
     */
    const newDeviceHandler = (): void => {
        modalHandler(false);
        props.clearPayload();
    };

    return (
        <Dialog
            open={isVisible}
            onClose={(): void => modalHandler(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="device-error-dialog"
            data-testid="error-modal"
        >
            <DialogTitle sx={{ px: 3, pt: 3, pb: 1 }}>
                <Typography variant={'h6'} fontWeight={600} data-testid="error-modal-title">
                    <CustomTransComponent translationKey={'ADD_DEVICE_FORM:ERROR_MODAL_TITLE'} />
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ px: 3, pb: 2 }}>
                <Typography variant={'body1'} mb={1} data-testid="error-modal-message">
                    <CustomTransComponent translationKey={'COMMON:ERROR_MSG'} />
                </Typography>

                {props?.messages?.map((message: string, index: number) => (
                    <Typography
                        key={`${index}-${message}`}
                        variant={'body1'}
                        mb={1}
                        data-testid={`error-modal-detail-${index}`}
                    >
                        &quot;{message}&quot;
                    </Typography>
                ))}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 3 }}>
                <Stack
                    direction={sm ? 'row' : 'column'}
                    spacing={2}
                    width="100%"
                    justifyContent="space-between"
                    alignItems={sm ? 'center' : 'stretch'}
                    className={classes.MuiDialogActionsRoot}
                >
                    <Button
                        variant="outlined"
                        onClick={(): void => modalHandler(false)}
                        data-test="cancel-button"
                        data-testid="error-cancel-btn"
                        sx={{
                            // minWidth: sm ? '120px' : '100%',
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: theme.palette.primary[50],
                                borderColor: theme.palette.primary.main,
                            },
                        }}
                    >
                        <CustomTransComponent translationKey={'COMMON:CANCEL'} />
                    </Button>

                    <Stack
                        direction={sm ? 'row' : 'column'}
                        spacing={2}
                        width={sm ? 'auto' : '100%'}
                        justifyContent="flex-end"
                    >
                        {!props.deviceId && (
                            <Button
                                variant="outlined"
                                onClick={newDeviceHandler}
                                data-test="add-new-device-btn"
                                data-testid="error-add-new-device-btn"
                                sx={{
                                    minWidth: sm ? '180px' : '100%',
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary[50],
                                    },
                                }}
                            >
                                <CustomTransComponent translationKey={'DEVICE_FORM_COMMON:ADD_NEW_DEVICE'} />
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={props.retryHandler}
                            data-test="try-again-btn"
                            data-testid="error-try-again-btn"
                            sx={{
                                minWidth: sm ? '140px' : '100%',
                                textTransform: 'none',
                                fontWeight: 600,
                                backgroundColor: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                            }}
                        >
                            <CustomTransComponent translationKey={'COMMON:TRY_AGAIN'} />
                        </Button>
                    </Stack>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
